import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase, updateProfile } from '@/lib/supabase';
import { uploadMedia } from '@/lib/cloudflare';
import { Camera, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ProfileForm {
  username: string;
  full_name: string;
  avatar_url: string | null;
  banner_url: string | null;
  bio: string;
  location: string;
  website: string;
  interests: string[];
  social_links: {
    twitter?: string;
    linkedin?: string;
    instagram?: string;
    github?: string;
  };
}

export function EditProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [interestInput, setInterestInput] = useState('');
  const [form, setForm] = useState<ProfileForm>({
    username: '',
    full_name: '',
    avatar_url: null,
    banner_url: null,
    bio: '',
    location: '',
    website: '',
    interests: [],
    social_links: {}
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      if (profile) {
        setForm({
          username: profile.username || '',
          full_name: profile.full_name || '',
          avatar_url: profile.avatar_url,
          banner_url: profile.banner_url,
          bio: profile.bio || '',
          location: profile.location || '',
          website: profile.website || '',
          interests: profile.interests || [],
          social_links: profile.social_links || {}
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file: File, type: 'avatar' | 'banner') => {
    try {
      const url = await uploadMedia(file);
      setForm(prev => ({
        ...prev,
        [type === 'avatar' ? 'avatar_url' : 'banner_url']: url
      }));
    } catch (error) {
      console.error(`Error uploading ${type}:`, error);
      setError(`Failed to upload ${type}`);
    }
  };

  const handleAddInterest = () => {
    const interest = interestInput.trim().toLowerCase();
    if (interest && !form.interests.includes(interest)) {
      setForm(prev => ({
        ...prev,
        interests: [...prev.interests, interest]
      }));
      setInterestInput('');
    }
  };

  const handleRemoveInterest = (interestToRemove: string) => {
    setForm(prev => ({
      ...prev,
      interests: prev.interests.filter(interest => interest !== interestToRemove)
    }));
  };

  const handleSave = async () => {
    if (!user) return;
    
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const success = await updateProfile(user.id, {
        ...form,
        updated_at: new Date().toISOString()
      });

      if (success) {
        setSuccess(true);
        setTimeout(() => navigate(`/profile/${user.id}`), 1500);
      } else {
        throw new Error('Failed to update profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="w-8 h-8 text-royal-purple animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="bg-white dark:bg-dark-paper rounded-xl shadow-sm overflow-hidden">
        {/* Banner */}
        <div className="relative h-48 bg-gradient-to-r from-royal-purple/20 to-royal-purple/10">
          {form.banner_url && (
            <img
              src={form.banner_url}
              alt="Profile banner"
              className="w-full h-full object-cover"
            />
          )}
          <button
            onClick={() => bannerInputRef.current?.click()}
            className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity group"
          >
            <div className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-dark-paper rounded-lg text-gray-700 dark:text-gray-300">
              <Camera className="w-5 h-5" />
              <span>Change Banner</span>
            </div>
          </button>
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImageUpload(file, 'banner');
            }}
            className="hidden"
          />
        </div>

        {/* Avatar */}
        <div className="relative -mt-16 ml-6">
          <div className="relative w-32 h-32">
            <img
              src={form.avatar_url || `https://api.dicebear.com/7.x/initials/svg?seed=${form.full_name}`}
              alt={form.full_name}
              className="w-full h-full rounded-full ring-4 ring-white dark:ring-dark-paper object-cover"
            />
            <button
              onClick={() => avatarInputRef.current?.click()}
              className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity rounded-full"
            >
              <Camera className="w-6 h-6 text-white" />
            </button>
          </div>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImageUpload(file, 'avatar');
            }}
            className="hidden"
          />
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400">
              Profile updated successfully! Redirecting...
            </div>
          )}

          <div className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={form.username}
                  onChange={(e) => setForm(prev => ({ ...prev, username: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={form.full_name}
                  onChange={(e) => setForm(prev => ({ ...prev, full_name: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bio
              </label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm(prev => ({ ...prev, bio: e.target.value }))}
                rows={4}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
              />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={form.location}
                  onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setForm(prev => ({ ...prev, website: e.target.value }))}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Interests
              </label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={interestInput}
                    onChange={(e) => setInterestInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddInterest();
                      }
                    }}
                    placeholder="Add interests..."
                    className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                  />
                  <button
                    type="button"
                    onClick={handleAddInterest}
                    className="px-4 py-2 bg-gray-100 dark:bg-dark-elevated text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-dark-paper transition-colors"
                  >
                    Add
                  </button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {form.interests.map((interest) => (
                    <span
                      key={interest}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-royal-purple/10 dark:bg-royal-purple/20 text-royal-purple rounded-full text-sm"
                    >
                      {interest}
                      <button
                        type="button"
                        onClick={() => handleRemoveInterest(interest)}
                        className="p-0.5 hover:bg-royal-purple/20 dark:hover:bg-royal-purple/30 rounded-full transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Social Links
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                {['twitter', 'linkedin', 'instagram', 'github'].map((platform) => (
                  <div key={platform}>
                    <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1 capitalize">
                      {platform}
                    </label>
                    <input
                      type="url"
                      value={form.social_links[platform as keyof typeof form.social_links] || ''}
                      onChange={(e) => setForm(prev => ({
                        ...prev,
                        social_links: {
                          ...prev.social_links,
                          [platform]: e.target.value
                        }
                      }))}
                      placeholder={`https://${platform}.com/username`}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-royal-purple focus:border-transparent dark:bg-dark-elevated dark:border-dark-border dark:text-gray-100 dark:placeholder-gray-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-dark-elevated border-t dark:border-dark-border">
          <div className="flex justify-end gap-3">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-paper rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={cn(
                "px-4 py-2 rounded-lg font-medium transition-colors",
                saving
                  ? "bg-gray-300 dark:bg-gray-700 cursor-not-allowed"
                  : "bg-royal-purple text-white hover:bg-royal-purple/90"
              )}
            >
              {saving ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </div>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}